import { useAuth } from "../../context/AuthContext";
import React, { useState, useMemo } from 'react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { AutomationRunsBatchesPage } from './automation-runs-batches-page';
import {
  ArrowLeft,
  Search,
  Users,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Check,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { listWorkflows, getWorkflow, listWorkflowRuns, listWorkflowVersions, WorkflowRunItem, getWorkflowStats, getWorkflowNodeStats } from './api';
// Runtime graph is kept separate from the editable Studio canvas: this view is
// read-only telemetry and drives the existing run filters.
import { WorkflowRuntimeGraph } from './components/OccupancyChips';
import { CallerJourneyDrawer } from './components/CallerJourneyDrawer';
import { OperatorResolutionModal } from './components/OperatorResolutionModal';
import { formatNextAction, nodeLabel } from './history-display';

const ATTENTION_STATUSES = ['ended_no_match', 'exhausted', 'failed'];

const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

export function AutomationRunsPage() {
  const { user } = useAuth() as any;
  const userTz = user?.timezone;
  const [searchParams] = useSearchParams();
  const selectedWorkflowId = searchParams.get('workflowId') || '';
  const selectedScheduleId = searchParams.get('scheduleId') || '';

  if (!selectedWorkflowId) {
    return <Navigate to="/home/automation" replace />;
  }
  if (!selectedScheduleId) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <AutomationRunsBatchesPage workflowId={selectedWorkflowId} />
      </div>
    );
  }
  return <AutomationRunsDetailPage workflowId={selectedWorkflowId} scheduleId={selectedScheduleId} />;
}

function AutomationRunsDetailPage({

  workflowId: selectedWorkflowId,
  scheduleId: selectedScheduleId,
}: {
  workflowId: string;
  scheduleId: string;
}) {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth() as any;
  const userTz = user?.timezone;

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCallerForJourney, setSelectedCallerForJourney] = useState<WorkflowRunItem | null>(null);
  const [selectedCallerForResolve, setSelectedCallerForResolve] = useState<WorkflowRunItem | null>(null);

  const { data: workflows = [] } = useQuery({
    queryKey: ['call-workflows'],
    queryFn: () => listWorkflows(),
  });

  // Fetch Selected Workflow Definition (Nodes, Edges)
  const { data: workflowData } = useQuery({
    queryKey: ['call-workflow', selectedWorkflowId],
    queryFn: () => getWorkflow(selectedWorkflowId),
    enabled: !!selectedWorkflowId,
  });

  const { data: workflowVersions = [] } = useQuery({
    queryKey: ['call-workflow-versions', selectedWorkflowId],
    queryFn: () => listWorkflowVersions(selectedWorkflowId),
    enabled: !!selectedWorkflowId,
  });

  // Occupancy chips: how many runs currently sit on each node.
  const { data: nodeStatsData } = useQuery({
    queryKey: ['workflowNodeStats', selectedWorkflowId, selectedScheduleId],
    queryFn: () => getWorkflowNodeStats(selectedWorkflowId, selectedScheduleId),
    enabled: !!selectedWorkflowId && !!selectedScheduleId,
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const nodeStats = nodeStatsData?.node_stats || {};

  // 4. Fetch overall KPIs
  const { data: statsData } = useQuery({
    queryKey: ['workflowStats', selectedWorkflowId, selectedScheduleId],
    queryFn: () => getWorkflowStats(selectedWorkflowId, undefined, selectedScheduleId),
    enabled: !!selectedWorkflowId && !!selectedScheduleId,
    staleTime: THREE_HOURS_MS,
    refetchInterval: THREE_HOURS_MS,
    refetchOnWindowFocus: false,
  });

  // Compute Top KPI Metrics
  const kpis = useMemo(() => {
    if (!statsData) {
      return { total: 0, completed: 0, inProgress: 0, needsAttention: 0, completedPct: '0', inProgressPct: '0' };
    }
    const total = statsData.total;
    const completed = statsData.stats.completed || 0;
    
    const active = statsData.stats.active || 0;
    const scheduled = statsData.stats.scheduled || 0;
    const inProgress = active + scheduled;

    const endedNoMatch = statsData.stats.ended_no_match || 0;
    const exhausted = statsData.stats.exhausted || 0;
    const failed = statsData.stats.failed || 0;
    const needsAttention = endedNoMatch + exhausted + failed;

    const completedPct = total > 0 ? ((completed / total) * 100).toFixed(1) : '0';
    const inProgressPct = total > 0 ? ((inProgress / total) * 100).toFixed(1) : '0';

    return { total, completed, inProgress, needsAttention, completedPct, inProgressPct };
  }, [statsData]);

  // 5. Fetch Paginated Runs based on active filters
  const { data: runsResponse, isLoading: runsLoading } = useQuery({
    queryKey: ['workflowRunsList', selectedWorkflowId, selectedScheduleId, currentPage, pageSize, searchTerm, selectedStatuses, selectedStages],
    queryFn: () => listWorkflowRuns(selectedWorkflowId, {
      page: currentPage,
      page_size: pageSize,
      search: searchTerm,
      origin_schedule_id: selectedScheduleId,
      status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      current_node_id: selectedStages.length > 0 ? selectedStages : undefined,
    }),
    enabled: !!selectedWorkflowId && !!selectedScheduleId,
  });

  // Multi-Select Stage Toggling
  const handleToggleStage = (stageId: string) => {
    setSelectedStages((prev) => {
      if (prev.includes(stageId)) {
        return prev.filter((id) => id !== stageId);
      } else {
        return [...prev, stageId];
      }
    });
    setCurrentPage(1);
  };

  // Multi-Select Status Toggling
  const handleToggleStatus = (statusKey: string) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(statusKey)) {
        return prev.filter((s) => s !== statusKey);
      } else {
        return [...prev, statusKey];
      }
    });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedStatuses([]);
    setSelectedStages([]);
    setCurrentPage(1);
  };

  // Pagination Calculation
  const paginatedCallers = runsResponse?.items || [];
  const totalEntries = runsResponse?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));
  const validPage = Math.min(currentPage, totalPages);
  
  const filteredCallersLength = totalEntries;

  const currentWfName = workflowData?.name || 'Selected Workflow';
  // Runs execute against an immutable published snapshot, while `workflowData`
  // is the editable draft and may have moved on (or be empty for legacy data).
  // Use the exact version pinned by this schedule's run for runtime telemetry.
  const pinnedVersionNumber = runsResponse?.items?.[0]?.workflow_version;
  const runtimeDefinition =
    workflowVersions.find((version) => version.workflow_version === pinnedVersionNumber)
    || workflowVersions[0]
    || workflowData;
  const runtimeNodes = runtimeDefinition?.nodes || [];
  const runtimeEdges = runtimeDefinition?.edges || [];

  return (
    <div className="w-full min-h-full p-6 pb-20 flex flex-col gap-6">
      {/* Top Heading Pill (Left Icon Back Button, Shifted Name, Larger Typography) */}
      <div className="bg-elevated border border-line rounded-2xl p-4.5 shadow-sm flex flex-wrap items-center justify-between gap-4">
        {/* Left Side: Back Icon + Workflow Name & Status */}
        <div className="flex items-center gap-3.5 min-w-0">
          <button
            onClick={() => navigate(`/home/automation/runs?workflowId=${encodeURIComponent(selectedWorkflowId)}`)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-line bg-elevated text-ink-2 shadow-2xs hover:bg-soft hover:text-ink transition-colors"
            title="Back to batches"
          >
            <ArrowLeft className="h-4.5 w-4.5" />
          </button>

          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-success shrink-0"></span>
              <h1 className="text-lg font-black text-ink tracking-tight truncate">
                {currentWfName}
              </h1>
              <span className="inline-flex items-center rounded-full bg-success-soft border border-success/30 px-2.5 py-0.5 text-xs font-bold text-success uppercase tracking-wider">
                {workflowData?.status || 'ACTIVE'}
              </span>
            </div>
            <p className="text-xs text-ink-3 font-medium mt-0.5">
              Status: <strong className="text-success font-bold">ACTIVE</strong> ·{' '}
              <span className="text-ink-2">{kpis.total.toLocaleString()} Callers Enrolled</span>
              {' · '}
              <span className="font-mono text-ink-3">{selectedScheduleId}</span>
            </p>
          </div>
        </div>

        {/* Right Side: Switch Workflow + Open Studio */}
        <div className="flex items-center gap-2.5">
          <select
            value={selectedWorkflowId}
            onChange={(e) => {
              setSearchParams({ workflowId: e.target.value });
            }}
            className="rounded-xl border border-line bg-elevated px-3 py-1.5 text-xs font-medium text-ink-2 shadow-2xs focus:border-brand focus:outline-none"
          >
            {workflows.map((wf) => (
              <option key={wf.id} value={wf.id}>
                {wf.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => navigate(`/home/create-automation?id=${selectedWorkflowId}`)}
            className="rounded-xl bg-ink px-3.5 py-1.5 text-xs font-semibold text-canvas shadow-sm hover:bg-ink-2 transition-colors"
          >
            Open Studio
          </button>
        </div>
      </div>

      {/* 4 Clickable Top KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        {/* Total Callers -> Resets filter */}
        <div
          onClick={() => handleResetFilters()}
          className={`rounded-2xl border p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${
            selectedStatuses.length === 0 && selectedStages.length === 0
              ? 'border-ink bg-elevated ring-2 ring-ink/10'
              : 'border-line bg-elevated hover:border-ink/40'
          }`}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-ink-3">Total Callers</div>
          <div className="mt-0.5 text-xl font-black text-ink">{kpis.total.toLocaleString()}</div>
          <div className="text-[11px] font-medium text-ink-3">100% Enrolled · Click to reset</div>
        </div>

        {/* Completed Sequence -> Multi-filter toggle */}
        <div
          onClick={() => handleToggleStatus('completed')}
          className={`rounded-2xl border p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${
            selectedStatuses.includes('completed')
              ? 'border-success bg-success-soft/40 ring-2 ring-success/20'
              : 'border-line bg-elevated hover:border-success/40'
          }`}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-success flex items-center justify-between">
            <span>Completed Sequence</span>
            {selectedStatuses.includes('completed') && <Check className="h-3 w-3" />}
          </div>
          <div className="mt-0.5 text-xl font-black text-success">{kpis.completed.toLocaleString()}</div>
          <div className="text-[11px] font-medium text-success">
            {kpis.completedPct}% Overall Conversion
          </div>
        </div>

        {/* In-Progress Follow-up -> Multi-filter toggle */}
        <div
          onClick={() => handleToggleStatus('active')}
          className={`rounded-2xl border p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${
            selectedStatuses.includes('active')
              ? 'border-info bg-info-soft/40 ring-2 ring-info/20'
              : 'border-line bg-elevated hover:border-info/40'
          }`}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-info flex items-center justify-between">
            <span>In-Progress Follow-up</span>
            {selectedStatuses.includes('active') && <Check className="h-3 w-3" />}
          </div>
          <div className="mt-0.5 text-xl font-black text-info">{kpis.inProgress.toLocaleString()}</div>
          <div className="text-[11px] font-medium text-info">{kpis.inProgressPct}% Scheduled Sequences</div>
        </div>

        {/* Needs Attention -> Multi-filter toggle */}
        <div
          onClick={() => {
            const allOn = ATTENTION_STATUSES.every((status) => selectedStatuses.includes(status));
            setSelectedStatuses((prev) => {
              if (allOn) return prev.filter((status) => !ATTENTION_STATUSES.includes(status));
              return [...new Set([...prev.filter((status) => !ATTENTION_STATUSES.includes(status)), ...ATTENTION_STATUSES])];
            });
            setCurrentPage(1);
          }}
          className={`rounded-2xl border p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${
            ATTENTION_STATUSES.some((status) => selectedStatuses.includes(status))
              ? 'border-warning bg-warning-soft/40 ring-2 ring-warning/20'
              : 'border-line bg-elevated hover:border-warning/40'
          }`}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-warning flex items-center justify-between">
            <span>Needs Attention</span>
            {ATTENTION_STATUSES.some((status) => selectedStatuses.includes(status)) && <Check className="h-3 w-3" />}
          </div>
          <div className="mt-0.5 text-xl font-black text-warning">{kpis.needsAttention.toLocaleString()}</div>
          <div className="text-[11px] font-medium text-warning">Review Queue / Exceptions</div>
        </div>
      </div>

      {workflowData && (
        <WorkflowRuntimeGraph
          nodes={runtimeNodes}
          edges={runtimeEdges}
          nodeStats={nodeStats}
          selectedStages={selectedStages}
          totalCallers={kpis.total}
          onToggleStage={handleToggleStage}
          onReset={handleResetFilters}
        />
      )}

      {/* 2. Dedicated Callers Table Card Section Below (No Inner Scroll, Clean Pagination) */}
      <div className="rounded-2xl border border-line bg-elevated shadow-sm flex flex-col">
        {/* Table Header Row */}
        <div className="p-4 pb-3 border-b border-line flex flex-wrap items-center justify-between gap-3 bg-elevated rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-soft text-ink-2">
              <Users className="h-4 w-4" />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-bold text-ink">Enrolled Callers</h3>
                {selectedStages.length > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-brand-soft border border-brand/30 px-2 py-0.5 text-[10px] font-bold text-brand">
                    {selectedStages.length} Stage{selectedStages.length === 1 ? '' : 's'} Selected
                  </span>
                )}
                {selectedStatuses.length > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-info-soft border border-info/30 px-2 py-0.5 text-[10px] font-bold text-info">
                    {selectedStatuses.length} Status{selectedStatuses.length === 1 ? '' : 'es'} Selected
                  </span>
                )}
              </div>
              <p className="text-[11px] text-ink-3">
                Full recipient records, active milestones, and operator resolution actions
              </p>
            </div>
          </div>

          <span className="text-xs font-semibold text-ink-3">
            Showing {filteredCallersLength} active record{filteredCallersLength === 1 ? '' : 's'}
          </span>
        </div>

        {/* Table Multi-Filter Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-soft px-4 py-2.5">
          <div className="flex flex-wrap items-center gap-2.5 flex-1 min-w-[280px]">
            {/* Search Input */}
            <div className="relative w-60">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-3" />
              <input
                type="text"
                placeholder="Search Name or Phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-lg border border-line bg-elevated py-1 pl-8 pr-2.5 text-xs text-ink shadow-2xs focus:border-brand focus:outline-none placeholder:text-ink-3"
              />
            </div>

            {/* Multi-Status Filter Pills */}
            <div className="flex flex-wrap items-center gap-1.5 pl-1 border-l border-line">
              {[
                { key: 'completed', label: '✓ Completed', activeClass: 'bg-success-soft text-success border-success/40' },
                { key: 'active', label: '⏳ In progress', activeClass: 'bg-info-soft text-info border-info/40' },
                { key: 'ended_no_match', label: '⚠️ Did not match', activeClass: 'bg-warning-soft text-warning border-warning/40' },
              ].map((pill) => {
                const isActive = selectedStatuses.includes(pill.key);
                return (
                  <button
                    key={pill.key}
                    onClick={() => handleToggleStatus(pill.key)}
                    className={`rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-all shadow-2xs ${
                      isActive
                        ? pill.activeClass
                        : 'border-line bg-elevated text-ink-2 hover:bg-soft'
                    }`}
                  >
                    {pill.label}
                  </button>
                );
              })}
            </div>

            {(searchTerm || selectedStages.length > 0 || selectedStatuses.length > 0) && (
              <button
                onClick={handleResetFilters}
                className="flex items-center gap-1 rounded-lg border border-line bg-elevated px-2.5 py-1 text-xs font-semibold text-ink-2 hover:bg-soft transition-colors shadow-2xs ml-auto"
              >
                <RotateCcw className="h-3 w-3" /> Reset All Filters
              </button>
            )}
          </div>
        </div>

        {/* Table Content (Natural Height, NO Inner Scroll) */}
        {runsLoading ? (
          <div className="p-16 text-center text-xs text-ink-3">Loading caller telemetry...</div>
        ) : paginatedCallers.length === 0 ? (
          <div className="p-16 text-center">
            <Users className="mx-auto h-8 w-8 text-ink-3 mb-2" />
            <p className="text-xs font-semibold text-ink-2">No caller records match active filters</p>
            <p className="text-[11px] text-ink-3 mt-1">Try clearing filters to view all enrolled callers.</p>
            <button
              onClick={handleResetFilters}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-line bg-elevated px-3 py-1 text-xs font-semibold text-ink-2 shadow-2xs hover:bg-soft"
            >
              <RotateCcw className="h-3 w-3" /> Reset Filters
            </button>
          </div>
        ) : (
          <div>
            <table className="w-full text-left text-xs text-ink-2">
              <thead className="border-b border-line bg-soft text-[11px] font-bold uppercase tracking-wider text-ink-3">
                <tr>
                  <th className="px-5 py-3">Contact Name & Phone</th>
                  <th className="px-5 py-3">Current Stage</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Last Outcome</th>
                  <th className="px-5 py-3 text-right">Next Call / Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line font-medium">
                {paginatedCallers.map((caller: WorkflowRunItem) => {
                  const callerVersion = workflowVersions.find((version) => version.workflow_version === caller.workflow_version);
                  const stageLabel = nodeLabel(callerVersion?.nodes?.length ? callerVersion.nodes : runtimeNodes, caller.current_node_id) || caller.current_node_id;
                  const isReviewNeeded = ATTENTION_STATUSES.includes(caller.status);
                  const isCompleted = caller.status === 'completed';
                  const isFailed = caller.status === 'failed' || caller.status === 'exhausted';

                  const statusClass = isCompleted
                    ? 'bg-success-soft text-success border-success/30'
                    : isFailed
                    ? 'bg-danger-soft text-danger border-danger/30'
                    : isReviewNeeded
                    ? 'bg-warning-soft text-warning border-warning/30'
                    : 'bg-info-soft text-info border-info/30';

                  const statusText = isCompleted
                    ? '✓ Completed'
                    : caller.status === 'failed'
                    ? '✕ Failed'
                    : caller.status === 'exhausted'
                    ? '⚠️ Limit reached'
                    : isReviewNeeded
                    ? '⚠️ Did not match'
                    : '⏳ In progress';

                  return (
                    <tr
                      key={caller.run_id}
                      onClick={() => setSelectedCallerForJourney(caller)}
                      className="cursor-pointer hover:bg-soft/80 transition-colors"
                    >
                      {/* Name & Phone */}
                      <td className="px-5 py-3.5">
                        <div className="font-bold text-ink">
                          {caller.contact_name || `Contact ${caller.contact_id || caller.run_id}`}
                        </div>
                        <div className="text-[11px] font-mono text-ink-3">
                          {caller.customer_phone || caller.contact_id || 'N/A'}
                        </div>
                      </td>

                      {/* Current Stage */}
                      <td className="px-5 py-3.5 text-ink-2 font-semibold">{stageLabel}</td>

                      {/* Status Badge */}
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${statusClass}`}>
                          {statusText}
                        </span>
                      </td>

                      {/* Last Outcome */}
                      <td className="px-5 py-3.5 text-ink-2">
                        {caller.last_outcome || '—'}
                      </td>

                      {/* Next Call / Actions */}
                      <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        {isReviewNeeded ? (
                          <button
                            onClick={() => setSelectedCallerForResolve(caller)}
                            className="rounded-lg border border-warning/40 bg-warning-soft px-3.5 py-1 text-xs font-bold text-warning hover:bg-warning-soft transition-colors shadow-2xs"
                          >
                            Resolve
                          </button>
                        ) : (
                          <div className="text-xs font-semibold text-ink-3">
                            {formatNextAction(caller.next_action, userTz)}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination Controls Footer (X entries visible, Y pages) */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-soft px-5 py-3 rounded-b-2xl">
              <div className="flex items-center gap-3 text-xs text-ink-3">
                <span>
                  Showing{' '}
                  <strong className="text-ink">
                    {totalEntries === 0 ? 0 : (validPage - 1) * pageSize + 1}
                  </strong>{' '}
                  to{' '}
                  <strong className="text-ink">
                    {Math.min(validPage * pageSize, totalEntries)}
                  </strong>{' '}
                  of <strong className="text-ink">{totalEntries}</strong> entries
                </span>

                <div className="flex items-center gap-1.5 pl-2 border-l border-line">
                  <span>Show:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="rounded-md border border-line bg-elevated px-2 py-0.5 text-xs font-medium text-ink-2 focus:outline-none"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* Page Number Buttons */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={validPage === 1}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-line bg-elevated text-ink-2 shadow-2xs hover:bg-soft disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <span className="px-2 text-xs font-semibold text-ink-2">
                  Page {validPage} of {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={validPage === totalPages}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-line bg-elevated text-ink-2 shadow-2xs hover:bg-soft disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Caller Journey Slide-Over Drawer */}
      <CallerJourneyDrawer
        isOpen={Boolean(selectedCallerForJourney)}
        onClose={() => setSelectedCallerForJourney(null)}
        caller={selectedCallerForJourney}
        workflowName={currentWfName}
        nodes={workflowData?.nodes || []}
        versions={workflowVersions}
      />

      {/* Operator Resolution Modal */}
      <OperatorResolutionModal
        isOpen={Boolean(selectedCallerForResolve)}
        onClose={() => setSelectedCallerForResolve(null)}
        caller={selectedCallerForResolve}
      />
    </div>
  );
}
